import { useUser } from "@clerk/clerk-react";
import { useEffect, useRef } from "react";
import { compressClerkMetadataUrls } from "@/lib/clerk-url-utils";

/**
 * Hook that automatically migrates user's Clerk metadata to use compressed URLs
 * Runs once per session when user is loaded
 * Fails gracefully - doesn't break app if migration fails
 */
export function useClerkMetadataMigration() {
  const { user, isLoaded } = useUser();
  const migrationAttempted = useRef(false);

  useEffect(() => {
    // Only run once per session
    if (!isLoaded || !user || migrationAttempted.current) {
      return;
    }

    migrationAttempted.current = true;

    const runMigration = async () => {
      try {
        const { metadata: updated, stats, needsUpdate } =
          compressClerkMetadataUrls(user.unsafeMetadata || {});

        if (!needsUpdate) {
          console.log("[URL Migration] No migration needed - URLs already compressed");
          return;
        }

        // Update Clerk metadata with compressed URLs
        await user.update({
          unsafeMetadata: updated,
        });

        // Log success for monitoring
        console.log(
          `[URL Migration] ✅ Success - Saved ${stats.charactersSaved} chars (${stats.reductionPercentage}% reduction)`,
        );
        console.log("[URL Migration] Stats:", {
          accounts: stats.totalAccounts,
          webAppUrls: stats.webAppUrlsCompressed,
          sheetUrls: stats.sheetUrlsCompressed,
          beforeSize: stats.beforeSize,
          afterSize: stats.afterSize,
        });
      } catch (error) {
        console.error("[URL Migration] ❌ Failed:", error);
        // Don't throw - app should continue to work even if migration fails
      }
    };

    runMigration();
  }, [user, isLoaded]);
}
